import { isObject } from '@bugbug/core/utils/toolbox';
import { useCallback, useMemo } from 'react';

import type { CreateOrUpdateStepDto, CreateOrUpdateStepFormData } from '../../steps.types';
import type { RtkErrorResponse } from '~/services/api/utils';

import type { Step } from '@bugbug/core/types/steps';
import type { Maybe } from '@bugbug/core/types/utils';
import { useCreateArtifactMutation } from '~/modules/artifacts/artifacts.api';
import { selectSingleProjectId } from '~/modules/project/project.selectors';
import { useAppSelector } from '~/modules/store';
import { usePlaybackCursorPosition } from '~/modules/tests/hooks/usePlaybackCursorPosition/usePlaybackCursorPosition';
import { showStepError, showStepFileError } from '~/modules/tests/tests.utils';
import { isInternalServerError, isNotFoundError } from '~/services/api/utils';
import { ACTIONS } from '~/services/toasts/internalServerError';

import { stepsApi } from '../../steps.api';

export const useUpdateStep = () => {
  const [updateStep, updateStepState] = stepsApi.useUpdateStepMutation();
  const [createArtifact] = useCreateArtifactMutation();
  const { refreshPlaybackCursorPosition } = usePlaybackCursorPosition();
  const currentProjectId = useAppSelector(selectSingleProjectId);

  const handleSaveStep = useCallback(
    async <TStep extends Step>({
      id,
      data,
    }: {
      id: TStep['id'];
      data: CreateOrUpdateStepFormData<TStep>;
    }) => {
      const dataReadyToSend: CreateOrUpdateStepDto<TStep> = {
        ...data,
        runTimeout: data.runTimeout ?? null,
        sleep: data.sleep ?? null,
        projectArtifactId: null,
        value: data.type === 'uploadFile' ? null : (data.value as Maybe<string>),
      };

      if (data.type === 'uploadFile' && isObject(data.value)) {
        try {
          const savedArtifact = await createArtifact({
            file: data.value,
            projectId: currentProjectId,
          }).unwrap();
          dataReadyToSend.projectArtifactId = savedArtifact.id;
          dataReadyToSend.value = null;
        } catch (error) {
          showStepFileError(
            ACTIONS.UPLOAD,
            error as RtkErrorResponse,
            isInternalServerError(error),
          );
          throw error;
        }
      }

      const result = updateStep({ id, data: dataReadyToSend }).unwrap();

      result
        .then(() => {
          refreshPlaybackCursorPosition();
        })
        .catch((error) => {
          showStepError(ACTIONS.UPDATE, error, isNotFoundError(error));
        });

      return result;
    },
    [updateStep, createArtifact, currentProjectId, refreshPlaybackCursorPosition],
  );

  return useMemo(
    () => [handleSaveStep, updateStepState] as const,
    [handleSaveStep, updateStepState],
  );
};
